import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  categoryId?: string;
  vendorId: string;
  sku?: string;
}

export interface CartItem {
  productId: string;
  product: Product;
  quantity: number;
  price: number;
}

export interface Order {
  id: string;
  items: CartItem[];
  totalAmount: number;
  status: 'pending' | 'completed' | 'failed';
  createdAt: Date;
  synced: boolean;
}

interface POSStore {
  isOnline: boolean;
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  initialize: () => Promise<void>;
  setOnlineStatus: (online: boolean) => void;
  addToCart: (product: Product, quantity: number) => void;
  removeFromCart: (productId: string) => void;
  updateCartQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  createOrder: () => Promise<Order | null>;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  syncOrders: () => Promise<void>;
}

export const usePOSStore = create<POSStore>((set, get) => ({
  isOnline: navigator.onLine,
  products: [],
  cart: [],
  orders: [],

  initialize: async (): Promise<void> => {
    // Load products from localStorage (simulating offline storage)
    const storedProducts = localStorage.getItem('webwaka_products');
    if (storedProducts) {
      set({ products: JSON.parse(storedProducts) as Product[] });
    }

    // Load orders from localStorage
    const storedOrders = localStorage.getItem('webwaka_orders');
    if (storedOrders) {
      set({ orders: JSON.parse(storedOrders) as Order[] });
    }

    // Listen for online/offline events
    window.addEventListener('online', () => {
      set({ isOnline: true });
      void get().syncOrders();
    });

    window.addEventListener('offline', () => {
      set({ isOnline: false });
    });
  },

  setOnlineStatus: (online: boolean): void => {
    set({ isOnline: online });
  },

  addToCart: (product: Product, quantity: number): void => {
    set((state) => {
      const existingItem = state.cart.find((item) => item.productId === product.id);

      if (existingItem) {
        return {
          cart: state.cart.map((item) =>
            item.productId === product.id ? { ...item, quantity: item.quantity + quantity } : item
          ),
        };
      }

      return {
        cart: [
          ...state.cart,
          {
            productId: product.id,
            product,
            quantity,
            price: product.price,
          },
        ],
      };
    });
  },

  removeFromCart: (productId: string): void => {
    set((state) => ({
      cart: state.cart.filter((item) => item.productId !== productId),
    }));
  },

  updateCartQuantity: (productId: string, quantity: number): void => {
    if (quantity <= 0) {
      get().removeFromCart(productId);
      return;
    }

    set((state) => ({
      cart: state.cart.map((item) => (item.productId === productId ? { ...item, quantity } : item)),
    }));
  },

  clearCart: (): void => {
    set({ cart: [] });
  },

  createOrder: async (): Promise<Order | null> => {
    const state = get();
    if (state.cart.length === 0) {
      return null;
    }

    const totalAmount = state.getCartTotal();
    const order: Order = {
      id: uuidv4(),
      items: [...state.cart],
      totalAmount,
      status: 'pending',
      createdAt: new Date(),
      synced: false,
    };

    set((state) => {
      const updatedOrders = [...state.orders, order];
      localStorage.setItem('webwaka_orders', JSON.stringify(updatedOrders));
      return { orders: updatedOrders, cart: [] };
    });

    // Try to sync immediately if online
    if (state.isOnline) {
      await get().syncOrders();
    }

    return order;
  },

  getCartTotal: (): number => {
    return get().cart.reduce((total, item) => total + item.price * item.quantity, 0);
  },

  getCartItemCount: (): number => {
    return get().cart.reduce((count, item) => count + item.quantity, 0);
  },

  syncOrders: async (): Promise<void> => {
    const state = get();
    const unsyncedOrders = state.orders.filter((order) => !order.synced);

    if (unsyncedOrders.length === 0 || !state.isOnline) {
      return;
    }

    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.webwaka.site';
    const token = localStorage.getItem('webwaka_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      for (const order of unsyncedOrders) {
        await fetch(`${apiUrl}/orders`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            items: order.items.map((i) => ({ productId: i.productId, quantity: i.quantity, price: i.price })),
            totalAmount: order.totalAmount,
          }),
        });
      }

      set((state) => ({
        orders: state.orders.map((order) => ({
          ...order,
          synced: true,
        })),
      }));

      // eslint-disable-next-line no-console
      console.log(`Synced ${unsyncedOrders.length} orders`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to sync orders:', error);
    }
  },
}));
